import { map, Observable, filter, merge, tap } from 'rxjs';
import { Pub, Sub, State } from '../../../state/state.abstract.ts';

const initStepperState = {
  activeStep: 0,
  finishFn: undefined,
  valid: true,
  action: undefined,
};

class PubStepper extends Pub<IStepperState> {
  nextStep(params: Pick<IStepperState, 'activeStep' | 'valid'>) {
    this.emit('nextStep', { ...params });
  }
  prevStep(params: Pick<IStepperState, 'activeStep'>) {
    this.emit('prevStep', { ...params });
  }
  validate(valid: IStepperState['valid']) {
    this.emit('validate', { valid });
  }
  setFinishFn(fn: IStepperState['finishFn']) {
    this.emit('setFinishFn', { finishFn: fn });
  }
  runFinishFn() {
    const { finishFn } = this.stream$.getValue();
    finishFn?.();
  }
  resetData() {
    this.emit('resetData', initStepperState);
  }
}

class SubStepper extends Sub<IStepperState> {
  protected actionHandlers(): Observable<IStepperState> {
    return merge(this.nextStep(), this.prevStep(), this.updateState()).pipe(
      tap((state) => {
        this.stream$.next({ ...state, action: 'internalUpdate' });
      }),
    );
  }

  private nextStep(): Observable<IStepperState> {
    return this.actionListener('nextStep').pipe(
      filter(({ valid }) => valid),
      map((state) => {
        return { ...state, activeStep: state.activeStep + 1 };
      }),
    );
  }
  private prevStep(): Observable<IStepperState> {
    return this.actionListener('prevStep').pipe(
      map((state) => {
        return { ...state, activeStep: state.activeStep - 1, valid: true };
      }),
    );
  }
  private updateState() {
    return this.actionListener(['validate']);
  }
}

class StepperState extends State<IStepperState> {
  pub = new PubStepper(this.stream$);
  sub = new SubStepper(this.stream$);
}

export const stepperState = new StepperState(initStepperState);

export interface IStepperState {
  activeStep: number;
  finishFn?: () => void;
  valid: boolean;
  action:
    | undefined
    | 'nextStep'
    | 'prevStep'
    | 'validate'
    | 'resetData'
    | 'setFinishFn'
    | 'internalUpdate';
}
